// Boot-time helpers used by main.jsx before React mounts.

// Probe the session against the cheapest authenticated JSON endpoint.
// verifyToken never answers 401 — it 302s to the login app, which a
// `redirect: 'manual'` fetch surfaces as `type === 'opaqueredirect'`.
//   'ok'              → session valid, backend reachable
//   'unauthenticated' → real auth failure → caller redirects to login
//   'network-error'   → backend unreachable (or 5xx) → caller enters offline mode
export async function probeAuth(base = '', fetchImpl = globalThis.fetch, timeoutMs = 6000) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetchImpl(`${base}/api/me`, {
      credentials: 'include',
      redirect: 'manual',
      cache: 'no-store',
      signal: ctrl.signal,
    });
    if (res.type === 'opaqueredirect' || res.status === 401 || res.status === 403) return 'unauthenticated';
    if (res.ok) return 'ok';
    return 'network-error';
  } catch {
    return 'network-error';
  } finally {
    clearTimeout(timer);
  }
}

// Minimal, dependency-free fallback page. Never blank, never a redirect.
export function renderOfflineEmpty(root, message) {
  if (!root) return;
  root.innerHTML = '';
  const wrap = document.createElement('div');
  wrap.className = 'od-offline-empty';
  wrap.setAttribute('role', 'status');

  const title = document.createElement('h1');
  title.textContent = 'Online-Doku';
  const p = document.createElement('p');
  p.textContent = message;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = 'Erneut versuchen';
  btn.addEventListener('click', () => window.location.reload());

  wrap.append(title, p, btn);
  root.appendChild(wrap);
}

// Wait between wrong offline passwords: 0, 0, 2 s, 4 s, … capped at 60 s.
export function unlockDelayMs(failures) {
  return failures < 2 ? 0 : Math.min(60000, 1000 * 2 ** (failures - 1));
}

// Unlock page of the encrypted offline copy. `unlock(password)` resolves on
// success and rejects on a wrong password; `onDone` starts the app.
export function renderOfflineUnlock(root, { unlock, onDone }) {
  if (!root) return;
  root.innerHTML = '';
  const wrap = document.createElement('form');
  wrap.className = 'od-offline-empty od-offline-unlock';
  const title = document.createElement('h1');
  title.textContent = 'Online-Doku';
  const p = document.createElement('p');
  p.textContent = 'Sie sind offline. Die gespeicherten Dokumente sind verschlüsselt – bitte Ihr Anmeldepasswort eingeben.';
  const input = document.createElement('input');
  input.type = 'password';
  input.autocomplete = 'current-password';
  input.placeholder = 'Passwort';
  input.setAttribute('aria-label', 'Passwort');
  const btn = document.createElement('button');
  btn.type = 'submit';
  btn.textContent = 'Entsperren';
  const msg = document.createElement('p');
  msg.className = 'od-offline-unlock-msg';
  msg.setAttribute('role', 'alert');
  let failures = 0;
  wrap.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!input.value) return;
    btn.disabled = true;
    msg.textContent = 'Wird entsperrt …';
    try {
      await unlock(input.value);
      input.value = '';
      onDone();
    } catch {
      failures += 1;
      const wait = unlockDelayMs(failures);
      msg.textContent = wait ? `Passwort falsch. Neuer Versuch in ${Math.round(wait / 1000)} s.` : 'Passwort falsch.';
      setTimeout(() => { btn.disabled = false; if (wait) msg.textContent = 'Passwort falsch.'; input.focus(); }, wait);
    }
  });
  wrap.append(title, p, input, btn, msg);
  root.appendChild(wrap);
  input.focus();
}
